import PropTypes from 'prop-types';
import qs from 'qs';
import React, { useState, useRef, useMemo } from 'react';
import { Configure } from 'react-instantsearch';

// Components
import { PagingToolbar, SearchBox } from '@search';
import SearchOptionsDesktop from '../../../../../algolia-search/js/components/sections/SearchOptionsDesktop';
import SearchOptionsMobile from '../../../../../algolia-search/js/components/sections/SearchOptionsMobile';
import SearchContainer from '@search/SearchContainer';

// Functionality
import { getAlgoliaIndexNameGlobal, urlToSearchState } from '@general/helpers/search.helpers';
import { useScreenMdDown, useScreenMinLg } from '@hooks/useResponsive';

const DEBOUNCE_TIME = 400;

const SearchInner = ({
    children,
    aroundLatLngViaIP = false,
    displayMap = false,
    displayPagingToolbar = true,
    displaySearchBox = true,
    displaySearchOptions = true,
    filters,
    hitsPerPage,
    index,
    initialSearchState: initialSearchStateProp,
    maxWidth = '1200px',
    optionalFilters = [],
    updateSearchStateUrl = false,
    RefinementComponent,
    AboveSearchBoxComponent,
}) => {
    const indexName = useMemo(() => {
        return getAlgoliaIndexNameGlobal(index);
    }, [index]);

    const [analyticsTags] = useState([]);
    const initialSearchState = useMemo(() => {
        if (initialSearchStateProp) {
            return {
                [indexName]: { ...initialSearchStateProp },
            };
        }

        const urlSearchState = urlToSearchState(window.location);
        if (Object.keys(urlSearchState).length > 0) {
            return {
                [indexName]: { ...urlSearchState },
            };
        }

        return {};
    }, [indexName, initialSearchStateProp]);

    const containerDesktop = useRef(null);
    const containerMobile = useRef(null);
    const isTabletDown = useScreenMdDown();
    const isDesktop = useScreenMinLg();

    const renderDesktop = () => (
        <div className="container-fluid px-0" style={{ maxWidth }}>
            <div className="d-flex" ref={containerDesktop}>
                <div className="w-100">
                    {displaySearchOptions && (
                        <div className="mb-3">
                            <SearchOptionsDesktop
                                displayMap={displayMap}
                                onSortByChange={selectedSortBy => {
                                    if (!updateSearchStateUrl) {
                                        return;
                                    }

                                    const urlParams = {
                                        ...qs.parse(window.location.search.slice(1)),
                                        sort: selectedSortBy.queryParam,
                                    };

                                    const url = `${window.location.pathname}?${qs.stringify(urlParams)}`;
                                    window.history.pushState('', '', url);
                                    if (window.ga) {
                                        window.ga('send', 'pageView', url);
                                    }
                                }}
                                onViewTypeChange={selectedViewType => {
                                    if (!updateSearchStateUrl) {
                                        return;
                                    }

                                    const urlParams = {
                                        ...qs.parse(window.location.search.slice(1)),
                                        view: selectedViewType,
                                    };

                                    const url = `${window.location.pathname}?${qs.stringify(urlParams)}`;
                                    window.history.pushState('', '', url);
                                    if (window.ga) {
                                        window.ga('send', 'pageView', url);
                                    }
                                }}
                            />
                        </div>
                    )}
                    <div>{children}</div>
                    {displayPagingToolbar && (
                        <div className="mt-1rem">
                            <PagingToolbar scrollToRef={containerDesktop} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderMobile = () => (
        <>
            <div ref={containerMobile}>
                {displaySearchOptions && (
                    <div className="mb-3">
                        <SearchOptionsMobile
                            onPageSizeChange={selectedPageSize => {
                                if (!updateSearchStateUrl) {
                                    return;
                                }

                                const urlParams = {
                                    ...qs.parse(window.location.search.slice(1)),
                                    perPage: selectedPageSize.hitsPerPage,
                                };

                                const url = `${window.location.pathname}?${qs.stringify(urlParams)}`;
                                window.history.pushState('', '', url);
                                if (window.ga) {
                                    window.ga('send', 'pageView', url);
                                }
                            }}
                            onSortByChange={selectedSortBy => {
                                if (!updateSearchStateUrl) {
                                    return;
                                }

                                const urlParams = {
                                    ...qs.parse(window.location.search.slice(1)),
                                    sort: selectedSortBy.queryParam,
                                };

                                const url = `${window.location.pathname}?${qs.stringify(urlParams)}`;
                                window.history.pushState('', '', url);
                                if (window.ga) {
                                    window.ga('send', 'pageView', url);
                                }
                            }}
                        />
                    </div>
                )}

                <div className="mb-3">{children}</div>

                {displayPagingToolbar && (
                    <div className="mt-1rem">
                        <PagingToolbar scrollToRef={containerMobile} />
                    </div>
                )}
            </div>
        </>
    );

    return (
        <SearchContainer
            key={index}
            index={index}
            initialSearchState={initialSearchState}
            onStateChange={({ uiState, setUiState }) => {
                setUiState(uiState);

                if (!updateSearchStateUrl) {
                    return;
                }

                const updatedSearchState = uiState[indexName] || {};
                delete updatedSearchState.configure;
                delete updatedSearchState.hitsPerPage;
                delete updatedSearchState.sortBy;

                let urlParams = qs.parse(window.location.search.slice(1));
                delete urlParams.refinementList;
                delete urlParams.query;
                urlParams = {
                    ...urlParams,
                    ...updatedSearchState,
                };

                const url = `${window.location.pathname}?${qs.stringify(urlParams)}`;
                window.history.pushState('', '', url);
                if (window.ga) {
                    window.ga('send', 'pageView', url);
                }
            }}
        >
            <Configure
                analyticsTags={analyticsTags}
                clickAnalytics
                hitsPerPage={hitsPerPage}
                filters={filters}
                maxValuesPerFacet={100}
                optionalFilters={optionalFilters}
                aroundLatLngViaIP={aroundLatLngViaIP}
            />

            {AboveSearchBoxComponent && <AboveSearchBoxComponent />}
            {displaySearchBox && (
                <div className="mb-3">
                    <SearchBox debounceTime={DEBOUNCE_TIME} placeholder="Search for..." />
                </div>
            )}

            {RefinementComponent && (
                <div className="mb-0.625rem">
                    <RefinementComponent />
                </div>
            )}

            {isDesktop && renderDesktop()}
            {isTabletDown && renderMobile()}
        </SearchContainer>
    );
};

SearchInner.propTypes = {
    children: PropTypes.node.isRequired,
    aroundLatLngViaIP: PropTypes.bool,
    displayMap: PropTypes.bool,
    displayPagingToolbar: PropTypes.bool,
    displaySearchBox: PropTypes.bool,
    displaySearchOptions: PropTypes.bool,
    filters: PropTypes.string,
    hitsPerPage: PropTypes.number,
    index: PropTypes.string.isRequired,
    initialSearchState: PropTypes.object,
    maxWidth: PropTypes.string,
    optionalFilters: PropTypes.array,
    updateSearchStateUrl: PropTypes.bool,
    RefinementComponent: PropTypes.func,
    AboveSearchBoxComponent: PropTypes.func,
};

export default SearchInner;
